// =============================================================================
// stores/uxControl.store.ts
// R2 UX Control Engine — workspace profiles, command palette, right-click,
// radial menu, custom buttons, AI-adaptive shortcuts
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Workspace Profile ─────────────────────────────────────────────────────────
export type WorkspaceProfileKey =
  | 'engineering' | 'music' | 'artist' | 'executive'
  | 'investor' | 'personal' | 'church' | 'travel';

export interface WorkspaceProfile {
  key: WorkspaceProfileKey;
  label: string;
  icon: string;
  accentColor: string;
  startupRoute: string;
  pinnedRoutes: string[];
  description: string;
}

export const PRESET_PROFILES: Record<WorkspaceProfileKey, WorkspaceProfile> = {
  engineering: {
    key: 'engineering', label: 'Engineering', icon: '⚡', accentColor: '#5eead4',
    startupRoute: 'dashboard',
    pinnedRoutes: ['projects', 'documents', 'clashdetection', 'r2-calendar', 'automation-studio'],
    description: 'BIM project management, clash detection, drawing tools',
  },
  music: {
    key: 'music', label: 'Music', icon: '🎵', accentColor: '#a78bfa',
    startupRoute: 'cie',
    pinnedRoutes: ['cie', 'creator', 'comms'],
    description: 'Creative tools, media, composition',
  },
  artist: {
    key: 'artist', label: 'Artist', icon: '🎨', accentColor: '#f472b6',
    startupRoute: 'creator',
    pinnedRoutes: ['creator', 'cie', 'documents'],
    description: 'Creative intelligence, assets, visual tools',
  },
  executive: {
    key: 'executive', label: 'Executive', icon: '👔', accentColor: '#60a5fa',
    startupRoute: 'dashboard',
    pinnedRoutes: ['dashboard', 'investor-relations', 'r2-calendar', 'r2-financial-command', 'health'],
    description: 'KPIs, investor relations, calendar, financial intelligence',
  },
  investor: {
    key: 'investor', label: 'Investor', icon: '📈', accentColor: '#fbbf24',
    startupRoute: 'r2-financial-command',
    pinnedRoutes: ['r2-financial-command', 'investor-relations', 'dashboard'],
    description: 'Markets, portfolio, investor CRM, funding tracking',
  },
  personal: {
    key: 'personal', label: 'Personal', icon: '🏠', accentColor: '#34d399',
    startupRoute: 'dashboard',
    pinnedRoutes: ['r2-safety', 'r2-calendar', 'r2-timesheet', 'comms'],
    description: 'Wellness, safety, personal tasks, timesheet',
  },
  church: {
    key: 'church', label: 'Church', icon: '✝', accentColor: '#fb923c',
    startupRoute: 'cie',
    pinnedRoutes: ['cie', 'comms', 'documents'],
    description: 'Community, communications, creative',
  },
  travel: {
    key: 'travel', label: 'Travel', icon: '✈', accentColor: '#818cf8',
    startupRoute: 'r2-calendar',
    pinnedRoutes: ['r2-calendar', 'r2-safety', 'documents'],
    description: 'Calendar, safety (lone worker), offline tools',
  },
};

// ── Custom Button ─────────────────────────────────────────────────────────────
export interface CustomButton {
  id: string;
  icon: string;       // lucide icon name or emoji
  name: string;
  description?: string;
  shortcut?: string;
  voiceCommand?: string;
  action: 'navigate' | 'macro' | 'ai-prompt' | 'script';
  actionValue: string; // route name, prompt text, or script
  color: string;
  pinned: boolean;
  order: number;
  animation?: 'pulse' | 'spin' | 'bounce' | 'none';
}

// ── Right-Click Command ───────────────────────────────────────────────────────
export interface RightClickCommand {
  id: string;
  label: string;
  icon: string;
  enabled: boolean;
  order: number;
  action: string;
  context?: string[]; // which apps/contexts show this
}

export const DEFAULT_RIGHT_CLICK_COMMANDS: RightClickCommand[] = [
  { id: 'ask-r2',        label: 'Ask R2…',          icon: 'Sparkles',    enabled: true,  order: 0, action: 'open-ai' },
  { id: 'recent',        label: 'Recent Projects',   icon: 'FolderOpen',  enabled: true,  order: 1, action: 'recent-projects' },
  { id: 'clipboard',     label: 'Smart Clipboard',   icon: 'Clipboard',   enabled: true,  order: 2, action: 'clipboard' },
  { id: 'screenshot',    label: 'Take Screenshot',   icon: 'Camera',      enabled: true,  order: 3, action: 'screenshot' },
  { id: 'create-task',   label: 'Create Task',        icon: 'PlusCircle',  enabled: true,  order: 4, action: 'create-task' },
  { id: 'recording',     label: 'Start Recording',   icon: 'Video',       enabled: true,  order: 5, action: 'start-recording' },
  { id: 'automation',    label: 'Run Automation',    icon: 'Zap',         enabled: true,  order: 6, action: 'run-automation' },
  { id: 'timesheet',     label: 'Open Timesheet',    icon: 'Clock',       enabled: true,  order: 7, action: 'navigate:r2-timesheet' },
  { id: 'ai-commands',   label: 'AI Commands',       icon: 'Bot',         enabled: true,  order: 8, action: 'open-ai' },
  { id: 'settings',      label: 'Settings',           icon: 'Settings',    enabled: true,  order: 9, action: 'navigate:settings' },
  { id: 'calculator',    label: 'Calculator',         icon: 'Calculator',  enabled: false, order: 10, action: 'calculator' },
  { id: 'expense',       label: 'Log Expense',        icon: 'Receipt',     enabled: false, order: 11, action: 'log-expense' },
];

// ── Usage Tracking (for AI-Adaptive UI) ──────────────────────────────────────
export interface UsageRecord {
  route: string;
  count: number;
  lastUsed: string; // ISO string
  hourDistribution: number[]; // 24h array
  dayDistribution: number[]; // 7-day array
}

// ── State ─────────────────────────────────────────────────────────────────────
interface UXControlState {
  // Workspace
  activeProfile: WorkspaceProfileKey;
  setActiveProfile: (k: WorkspaceProfileKey) => void;

  // Right-click
  rightClickEnabled: boolean;
  setRightClickEnabled: (v: boolean) => void;
  rightClickCommands: RightClickCommand[];
  setRightClickCommands: (cmds: RightClickCommand[]) => void;

  // Radial menu
  radialMenuEnabled: boolean;
  setRadialMenuEnabled: (v: boolean) => void;
  radialHoldMs: number;
  setRadialHoldMs: (ms: number) => void;

  // Command palette
  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;

  // Sidebar dock
  sidebarMode: 'docked-left' | 'docked-right' | 'floating' | 'auto-hide' | 'collapsed';
  setSidebarMode: (m: UXControlState['sidebarMode']) => void;

  // Custom buttons
  customButtons: CustomButton[];
  addCustomButton: (b: Omit<CustomButton, 'id' | 'order'>) => void;
  updateCustomButton: (id: string, updates: Partial<CustomButton>) => void;
  removeCustomButton: (id: string) => void;
  reorderButton: (id: string, direction: 'up' | 'down') => void;

  // AI-Adaptive usage
  usageRecords: Record<string, UsageRecord>;
  recordRouteUsage: (route: string) => void;
  getAdaptiveSuggestions: () => string[]; // top 5 routes by recent usage
}

export const useUXControlStore = create<UXControlState>()(
  persist(
    (set, get) => ({
      activeProfile: 'engineering',
      setActiveProfile: (k) => set({ activeProfile: k }),

      rightClickEnabled: true,
      setRightClickEnabled: (v) => set({ rightClickEnabled: v }),
      rightClickCommands: DEFAULT_RIGHT_CLICK_COMMANDS,
      setRightClickCommands: (cmds) => set({ rightClickCommands: cmds }),

      radialMenuEnabled: true,
      setRadialMenuEnabled: (v) => set({ radialMenuEnabled: v }),
      radialHoldMs: 400,
      setRadialHoldMs: (ms) => set({ radialHoldMs: Math.max(200, Math.min(1000, ms)) }),

      commandPaletteOpen: false,
      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      sidebarMode: 'docked-left',
      setSidebarMode: (m) => set({ sidebarMode: m }),

      customButtons: [],
      addCustomButton: (b) =>
        set((s) => ({
          customButtons: [
            ...s.customButtons,
            { ...b, id: `btn-${Date.now()}`, order: s.customButtons.length },
          ],
        })),
      updateCustomButton: (id, updates) =>
        set((s) => ({
          customButtons: s.customButtons.map((b) => (b.id === id ? { ...b, ...updates } : b)),
        })),
      removeCustomButton: (id) =>
        set((s) => ({ customButtons: s.customButtons.filter((b) => b.id !== id) })),
      reorderButton: (id, dir) =>
        set((s) => {
          const arr = [...s.customButtons].sort((a, b) => a.order - b.order);
          const idx = arr.findIndex((b) => b.id === id);
          if (idx < 0) return s;
          const swap = dir === 'up' ? idx - 1 : idx + 1;
          if (swap < 0 || swap >= arr.length) return s;
          [arr[idx]!.order, arr[swap]!.order] = [arr[swap]!.order, arr[idx]!.order];
          return { customButtons: arr };
        }),

      usageRecords: {},
      recordRouteUsage: (route) => {
        const now = new Date();
        set((s) => {
          const existing = s.usageRecords[route] ?? {
            route, count: 0, lastUsed: now.toISOString(),
            hourDistribution: new Array(24).fill(0),
            dayDistribution: new Array(7).fill(0),
          };
          const hourDist = [...existing.hourDistribution];
          const dayDist = [...existing.dayDistribution];
          hourDist[now.getHours()]! += 1;
          dayDist[now.getDay()]! += 1;
          return {
            usageRecords: {
              ...s.usageRecords,
              [route]: { ...existing, count: existing.count + 1, lastUsed: now.toISOString(), hourDistribution: hourDist, dayDistribution: dayDist },
            },
          };
        });
      },
      getAdaptiveSuggestions: () => {
        const { usageRecords } = get();
        const now = new Date();
        const currentHour = now.getHours();
        return Object.values(usageRecords)
          .sort((a, b) => {
            // Weight recent usage + current-hour affinity
            const aHourScore = (a.hourDistribution[currentHour] ?? 0) * 3;
            const bHourScore = (b.hourDistribution[currentHour] ?? 0) * 3;
            return (b.count + bHourScore) - (a.count + aHourScore);
          })
          .slice(0, 5)
          .map((r) => r.route);
      },
    }),
    { name: 'r2-ux-control' },
  ),
);
